import React, { useMemo } from 'react';
import { useFragment, graphql } from 'react-relay';
import AvatarGroup, { type AvatarProps } from '@atlaskit/avatar-group';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import type { UserConnectionReadView$key as UserConnectionReadViewFragment } from './__generated__/UserConnectionReadView.graphql';
import type { UserConnectionReadViewProps, NonNullableSelectedUserEdge } from './types.tsx';

/**
 * The UserConnectionReadView will show an AvatarGroup of the given JiraUserConnection.
 * @param props [UserConnectionReadViewProps](./types.tsx)
 */
export const UserConnectionReadView = ({
	avatarGroupOverrides,
	fragmentRef,
}: UserConnectionReadViewProps): React.JSX.Element => {
	const data = useFragment<UserConnectionReadViewFragment>(
		graphql`
			fragment UserConnectionReadView on JiraUserConnection {
				edges {
					node {
						accountId
						name
						picture
					}
				}
			}
		`,
		fragmentRef,
	);

	const transformedData: AvatarProps[] = useMemo(() => {
		const filteredEdges =
			data?.edges?.filter((edge): edge is NonNullableSelectedUserEdge => Boolean(edge?.node)) ?? [];

		return filteredEdges.map((edge) => ({
			name: edge.node.name,
			key: edge.node.accountId,
			src: edge.node.picture,
		}));
	}, [data?.edges]);

	if (transformedData.length === 0) {
		return <EmptyFieldText />;
	}

	return <AvatarGroup data={transformedData} overrides={avatarGroupOverrides} size="small" />;
};
